import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Divider,
    Fade,
    IconButton,
    Paper,
    Typography,
    useMediaQuery,
} from '@mui/material';
import BulkActionModal from './BulkActionModal';
import { Close } from '@mui/icons-material';

interface BulkActionsProps {
    gridRef?: any;
    selected: number[];
    selectedRows?: any[];
    onClearSelection?: () => void;
}

type Action = 'cancel' | 'approve' | 'decline';

const safeLower = (v: any) => String(v ?? '').toLowerCase();

const getRowStatus = (row: any) =>
    safeLower(
        row?.status ?? row?.bookingInfo?.status ?? row?.reservationStatus,
    );

const getRowId = (row: any) => {
    const n = Number(row?.id ?? row?.reservationId ?? row?._id);
    return Number.isFinite(n) ? n : null;
};

const uniq = (arr: number[]) => Array.from(new Set(arr));

const BulkActions = ({
    gridRef,
    selected,
    selectedRows = [],
    onClearSelection,
}: BulkActionsProps) => {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');

    const [modalOpen, setModalOpen] = useState(false);
    const [bulkAction, setBulkAction] = useState<Action>();

    const idsByStatus = useMemo(() => {
        const out: Record<
            'pending' | 'approved' | 'declined' | 'cancelled',
            number[]
        > = {
            pending: [],
            approved: [],
            declined: [],
            cancelled: [],
        };

        for (const r of selectedRows) {
            const id = getRowId(r);
            if (!id) continue;

            const s = getRowStatus(r);

            if (s === 'pending') out.pending.push(id);
            else if (s === 'approved') out.approved.push(id);
            else if (s === 'declined') out.declined.push(id);
            else if (s === 'cancelled') out.cancelled.push(id);
        }

        return out;
    }, [selectedRows]);

    const actionIds = useMemo(() => {
        return {
            approve: uniq(idsByStatus.pending),
            decline: uniq(idsByStatus.pending),
            cancel: uniq(idsByStatus.approved),
        };
    }, [idsByStatus]);

    const visible = useMemo(() => {
        return {
            approve: actionIds.approve.length > 0,
            decline: actionIds.decline.length > 0,
            cancel: actionIds.cancel.length > 0,
        };
    }, [actionIds]);

    const selectedCount = selected.length;

    const counts = useMemo(() => {
        const pending = idsByStatus.pending.length;
        const approved = idsByStatus.approved.length;
        const declined = idsByStatus.declined.length;
        const cancelled = idsByStatus.cancelled.length;

        return { pending, approved, declined, cancelled };
    }, [idsByStatus]);

    const noActions = !visible.approve && !visible.decline && !visible.cancel;

    const handleAction = (action: Action) => {
        setBulkAction(action);
        setModalOpen(true);
    };

    const handleClear = () => {
        onClearSelection?.();
        if (!onClearSelection) gridRef?.current?.setRowSelectionModel?.([]);
    };

    const idsForModal = useMemo(() => {
        if (!bulkAction) return [];
        if (bulkAction === 'approve') return actionIds.approve;
        if (bulkAction === 'decline') return actionIds.decline;
        if (bulkAction === 'cancel') return actionIds.cancel;
        return [];
    }, [bulkAction, actionIds]);

    const statusSummary = useMemo(() => {
        const parts: string[] = [];
        if (counts.pending) parts.push(`Pending: ${counts.pending}`);
        if (counts.approved) parts.push(`Approved: ${counts.approved}`);
        if (counts.declined) parts.push(`Declined: ${counts.declined}`);
        if (counts.cancelled) parts.push(`Cancelled: ${counts.cancelled}`);
        return parts.join(' · ');
    }, [counts]);

    return (
        <>
            <Fade in={selectedCount > 0 && !modalOpen} timeout={200}>
                <Box
                    sx={{
                        position: 'fixed',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 2000,
                        pointerEvents: 'none',
                    }}
                >
                    <Box
                        sx={{
                            height: 140,
                            background:
                                'linear-gradient(to top, rgba(0,0,0,0.16), rgba(0,0,0,0.08), rgba(0,0,0,0))',
                            pointerEvents: 'none',
                        }}
                    />

                    <Box
                        sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            bottom: 22,
                            display: 'flex',
                            justifyContent: 'center',
                            px: isMobile || isIpad ? 2 : 0,
                            pointerEvents: 'none',
                        }}
                    >
                        <Paper
                            elevation={0}
                            sx={{
                                pointerEvents: 'auto',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                px: 2.25,
                                py: 1.75,
                                borderRadius: 4,
                                width: isMobile || isIpad ? '100%' : 760,
                                maxWidth: 'calc(100% - 32px)',
                                backgroundColor: 'rgba(255,255,255,0.96)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(0,0,0,0.14)',
                                boxShadow:
                                    '0 18px 50px rgba(0,0,0,0.28), 0 6px 18px rgba(0,0,0,0.18)',
                                position: 'relative',
                                overflow: 'hidden',
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: 6,
                                }}
                            />

                            <IconButton
                                onClick={handleClear}
                                size='small'
                                sx={{ ml: 0.5 }}
                            >
                                <Close fontSize='small' />
                            </IconButton>

                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.35,
                                    minWidth: 0,
                                    flex: 1,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: 14,
                                        lineHeight: 1.1,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {selectedCount} reservation
                                    {selectedCount !== 1 ? 's' : ''} selected
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: 12.5,
                                        fontWeight: 600,
                                        color: noActions
                                            ? 'error.main'
                                            : 'text.secondary',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {noActions
                                        ? 'No actions available for the selected reservations'
                                        : statusSummary}
                                </Typography>
                            </Box>

                            <Divider
                                orientation='vertical'
                                flexItem
                                sx={{ mx: 0.25, opacity: 0.5 }}
                            />

                            {visible.approve && (
                                <Button
                                    size='small'
                                    variant='contained'
                                    onClick={() => handleAction('approve')}
                                    sx={{
                                        whiteSpace: 'nowrap',
                                        borderRadius: 2.25,
                                        px: 1.4,
                                        fontWeight: 800,
                                    }}
                                >
                                    Approve ({actionIds.approve.length})
                                </Button>
                            )}

                            {visible.decline && (
                                <Button
                                    size='small'
                                    variant='outlined'
                                    color='error'
                                    onClick={() => handleAction('decline')}
                                    sx={{
                                        whiteSpace: 'nowrap',
                                        borderRadius: 2.25,
                                        px: 1.4,
                                        fontWeight: 800,
                                        borderWidth: 2,
                                        '&:hover': { borderWidth: 2 },
                                    }}
                                >
                                    Decline ({actionIds.decline.length})
                                </Button>
                            )}

                            {visible.cancel && (
                                <Button
                                    size='small'
                                    variant='contained'
                                    color='error'
                                    onClick={() => handleAction('cancel')}
                                    sx={{
                                        whiteSpace: 'nowrap',
                                        borderRadius: 2.25,
                                        px: 1.4,
                                        fontWeight: 800,
                                    }}
                                >
                                    {actionIds.cancel.length === 1
                                        ? 'Cancel Reservation'
                                        : `Cancel (${actionIds.cancel.length}) Reservations`}
                                </Button>
                            )}
                        </Paper>
                    </Box>
                </Box>
            </Fade>

            <BulkActionModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                ids={idsForModal}
                action={bulkAction}
            />
        </>
    );
};

export default BulkActions;
